import { BigDecimal, Option, Schema } from 'effect';

import { Price, PriceFromApiSchema } from '@vs-shop/schemas/primitive/price';
import type { PendingCartLine } from './addToCart';

export type CartDrawerPendingLine = Pick<
  PendingCartLine,
  | 'id'
  | 'variantCode'
  | 'productName'
  | 'quantity'
  | 'totalPrice'
  | 'confirmationTarget'
  | 'unresolvedCause'
  | 'preview'
>;

export type CartDrawerPendingStatus = 'adding' | 'verifying' | 'unresolved';

export type CartDrawerProjectedPendingLine = {
  readonly id: number;
  readonly variantCode: string;
  readonly productName: CartDrawerPendingLine['productName'];
  readonly quantity: number;
  readonly totalPrice: CartDrawerPendingLine['totalPrice'];
  readonly status: CartDrawerPendingStatus;
  readonly preview?: CartDrawerPendingLine['preview'];
};

export type CartDrawerProjectedLine<ConfirmedLine> =
  | {
      readonly kind: 'confirmed';
      readonly item: ConfirmedLine;
      readonly pending?: CartDrawerProjectedPendingLine;
    }
  | {
      readonly kind: 'pending';
      readonly pending: CartDrawerProjectedPendingLine;
    };

type VariantLine = {
  readonly variantCode: string;
};

const decodeApiPrice = Schema.decodeSync(PriceFromApiSchema);

const getPendingStatus = (line: CartDrawerPendingLine): CartDrawerPendingStatus =>
  Option.isSome(line.unresolvedCause)
    ? 'unresolved'
    : Option.isSome(line.confirmationTarget)
      ? 'verifying'
      : 'adding';

const preferPendingStatus = (
  current: CartDrawerPendingStatus,
  next: CartDrawerPendingStatus
): CartDrawerPendingStatus => {
  if (current === 'adding' || next === 'adding') return 'adding';
  if (current === 'unresolved' || next === 'unresolved') return 'unresolved';
  return 'verifying';
};

const preferPreview = (
  current: CartDrawerPendingLine['preview'],
  next: CartDrawerPendingLine['preview']
): CartDrawerPendingLine['preview'] => {
  if (current === undefined) return next;
  if (next === undefined) return current;

  const imageUrl = current.imageUrl ?? next.imageUrl;
  const ageRestricted =
    current.ageRestricted === true || next.ageRestricted === true
      ? true
      : (current.ageRestricted ?? next.ageRestricted);

  return {
    ...(imageUrl === undefined ? {} : { imageUrl }),
    ...(ageRestricted === undefined ? {} : { ageRestricted }),
  };
};

const mergePendingLines = (
  current: CartDrawerProjectedPendingLine,
  next: CartDrawerPendingLine
): CartDrawerProjectedPendingLine => {
  const preview = preferPreview(current.preview, next.preview);

  return {
    ...current,
    quantity: current.quantity + next.quantity,
    totalPrice: Price(BigDecimal.sum(current.totalPrice, next.totalPrice)),
    status: preferPendingStatus(current.status, getPendingStatus(next)),
    ...(preview === undefined ? {} : { preview }),
  };
};

const groupPendingLines = (
  pendingLines: ReadonlyArray<CartDrawerPendingLine>
): ReadonlyArray<CartDrawerProjectedPendingLine> => {
  const grouped = new Map<string, CartDrawerProjectedPendingLine>();

  for (const line of pendingLines) {
    const current = grouped.get(line.variantCode);
    grouped.set(
      line.variantCode,
      current === undefined
        ? {
            id: line.id,
            variantCode: line.variantCode,
            productName: line.productName,
            quantity: line.quantity,
            totalPrice: line.totalPrice,
            status: getPendingStatus(line),
            ...(line.preview === undefined ? {} : { preview: line.preview }),
          }
        : mergePendingLines(current, line)
    );
  }

  return [...grouped.values()];
};

/** Combine pending adds by variant and place them beside a confirmed variant. */
export const projectCartDrawerLines = <ConfirmedLine extends VariantLine>(
  confirmedLines: ReadonlyArray<ConfirmedLine>,
  pendingLines: ReadonlyArray<CartDrawerPendingLine>
): ReadonlyArray<CartDrawerProjectedLine<ConfirmedLine>> => {
  const groupedPending = groupPendingLines(pendingLines);
  const pendingByVariant = new Map(groupedPending.map((line) => [line.variantCode, line]));
  const confirmedCounts = new Map<string, number>();
  for (const line of confirmedLines) {
    confirmedCounts.set(line.variantCode, (confirmedCounts.get(line.variantCode) ?? 0) + 1);
  }
  const projectedConfirmed = confirmedLines.map((item): CartDrawerProjectedLine<ConfirmedLine> => {
    const pending = pendingByVariant.get(item.variantCode);
    return pending === undefined || confirmedCounts.get(item.variantCode) !== 1
      ? { kind: 'confirmed', item }
      : { kind: 'confirmed', item, pending };
  });
  const projectedPending = groupedPending
    .filter((line) => confirmedCounts.get(line.variantCode) !== 1)
    .map((pending): CartDrawerProjectedLine<ConfirmedLine> => ({ kind: 'pending', pending }));

  return [...projectedConfirmed, ...projectedPending];
};

/** A cart write is live until it has a server quantity or an unresolved outcome. */
export const hasLiveCartWrite = (
  lines: ReadonlyArray<Pick<PendingCartLine, 'confirmationTarget' | 'unresolvedCause'>>
): boolean =>
  lines.some(
    (line) => Option.isNone(line.confirmationTarget) && Option.isNone(line.unresolvedCause)
  );

/** A cart write has an uncertain outcome when reconciliation recorded a cause. */
export const hasUncertainCartWrite = (
  lines: ReadonlyArray<Pick<PendingCartLine, 'unresolvedCause'>>
): boolean => lines.some((line) => Option.isSome(line.unresolvedCause));

/** Sum server-confirmed item cents and visible pending prices in currency units. */
export const getProvisionalItemsTotal = (
  confirmedItemsTotal: number,
  pendingLines: ReadonlyArray<CartDrawerPendingLine>
): BigDecimal.BigDecimal =>
  BigDecimal.sum(
    decodeApiPrice(confirmedItemsTotal),
    BigDecimal.sumAll(pendingLines.map((line) => line.totalPrice))
  );

/** Derive shared drawer state before a tenant renders its own Cart presentation. */
export const projectCartDrawerState = <ConfirmedLine extends VariantLine>(
  confirmedLines: ReadonlyArray<ConfirmedLine>,
  pendingLines: ReadonlyArray<CartDrawerPendingLine>,
  confirmedItemsTotal?: number
) => {
  const lines = projectCartDrawerLines(confirmedLines, pendingLines);

  return {
    lines,
    hasLines: lines.length > 0,
    hasLiveWrite: hasLiveCartWrite(pendingLines),
    hasUncertainWrite: hasUncertainCartWrite(pendingLines),
    provisionalItemsTotal:
      pendingLines.length === 0 || confirmedItemsTotal === undefined
        ? Option.none<BigDecimal.BigDecimal>()
        : Option.some(getProvisionalItemsTotal(confirmedItemsTotal, pendingLines)),
  };
};
